import { AfterViewInit, Component, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
// amCharts imports
import * as am5 from '@amcharts/amcharts5';
import * as am5percent from '@amcharts/amcharts5/percent';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { BaseChartService } from '../../../../shared/services/base-chart.service';
import { BarChartData } from '../interfaces/bar-chart-data.interface';
import { CommonService } from '../../../../shared/services/common.service';

/**
 * Funnel Graph Component
 * @author: PRachethan | 02-08-2025 
 */
@Component({
  selector: 'bi-funnel-graph',
  standalone: false,
  templateUrl: './funnel-graph.component.html',
  styleUrl: './funnel-graph.component.scss'
})
export class FunnelGraphComponent implements OnInit, AfterViewInit, OnDestroy {
  private root!: am5.Root;
  data: BarChartData[] = [];
  @Input() reportData: any = null;
  private isViewInitialized = false;

  constructor(
    private zone: NgZone,
    private _baseChartService: BaseChartService,
    private _commonService: CommonService) { }

  ngOnInit(): void {
    this.zone.runOutsideAngular(() => {
      this._commonService.graphReportState.subscribe((data: any) => {
        if (data && data.reportData && Array.isArray(data.reportData) && data.reportData.length > 0) {
          this.reportData = data;
          this.data = this._baseChartService.transformReportData(data);
          if (this.isViewInitialized) {
            this.updateChart(this.data);
          }
        } else {
        }
      });
    });
  }

  ngAfterViewInit(): void {
    this.isViewInitialized = true;
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0) {
      this.zone.runOutsideAngular(() => {
        this.data = this._baseChartService.transformReportData(this.reportData);
        this.updateChart(this.data);
      });
    }
  }

  ngOnChanges(): void {
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0 && this.isViewInitialized) {
      this.zone.runOutsideAngular(() => {
        this.data = this._baseChartService.transformReportData(this.reportData);
        this.updateChart(this.data);
      });
    }
  }

  createChart(data: BarChartData[]) {
    this.root = this._baseChartService.createRoot('funnel-chartdiv');
    this._baseChartService.attachExporting(this.root, {
      filePrefix: 'funnel',
      data: data
    });

    this.root.setThemes([am5themes_Animated.new(this.root)]);

    // Create main container
    const mainContainer = this.root.container.children.push(
      am5.Container.new(this.root, {
        width: am5.percent(100),
        height: am5.percent(100),
        layout: this.root.horizontalLayout
      })
    );

    // Create legend first (left side)
    let legend = mainContainer.children.push(
      am5.Legend.new(this.root, {
        width: am5.percent(20),
        layout: this.root.verticalLayout,
        centerY: am5.p50,
        y: am5.p50
      })
    );

    // Enhanced legend animation
    this._baseChartService.setupLegendAnimation(legend);

    // Create chart container (right side)
    const chartContainer = mainContainer.children.push(
      am5percent.SlicedChart.new(this.root, {
        width: am5.percent(80),
        height: am5.percent(100),
        paddingTop: 10,
        paddingBottom: 10,
        paddingLeft: 10,
        paddingRight: 10
      })
    );

    // Enhanced chart animation
    this._baseChartService.setupChartAnimation(chartContainer, 'elastic');

    let funnelSeries = chartContainer.series.push(
      am5percent.FunnelSeries.new(this.root, {
        alignLabels: true,
        orientation: "vertical",
        valueField: "value",
        categoryField: "category"
      })
    );

    // Enhanced series animation
    this._baseChartService.setupSeriesAnimation(funnelSeries, 'bounce');

    funnelSeries.slices.template.setAll({
      strokeOpacity: 0.5,
      strokeWidth: 2,
      stroke: am5.color(0xffffff)
    });

    funnelSeries.ticks.template.setAll({
      location: 1
    });

    // Configure tooltip properly with enhanced animation
    const tooltip = this._baseChartService.createStandardTooltip(this.root, 'funnel', {
      showPercentage: true,
      showValue: true,
      showCategory: true
    });
    funnelSeries.slices.template.set('tooltip', tooltip);

    // Transform data for funnel chart
    const categoryField = this._baseChartService.getCategoryField(data);
    const valueFields = this._baseChartService.getValueFields(data);
    const firstValueField = valueFields[0];

    const funnelData = data.map((item, index) => ({
      category: item[categoryField],
      value: Number(item[firstValueField]) || 0
    }));

    // Sort data by value in descending order for funnel effect
    funnelData.sort((a, b) => Number(b.value) - Number(a.value));

    // Set data
    funnelSeries.data.setAll(funnelData);

    legend.data.setAll(funnelSeries.dataItems);

    // Trigger enhanced chart animation
    setTimeout(() => {
      funnelSeries.appear(1200, 100);
    }, 100);
  }

  private updateChart(data: BarChartData[]) {
    if (this.root) {
      this._baseChartService.disposeChart(this.root);
    }
    this.createChart(data);

    // Update the chart data in the registry
    BaseChartService.updateActiveChartData(data);
  }

  ngOnDestroy(): void {
    this._baseChartService.disposeChart(this.root);
  }
}
